import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, 
  BookOpen, 
  ArrowRight, 
  Mail,
  BarChart3,
  MapPin,
  DollarSign,
  Briefcase,
  Calendar,
  Users,
  Search
} from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import { generateArticleSchema, generateOrganizationSchema, generateMultipleSchemas } from '@/lib/schema';
import { LeaderboardAd, InContentAd } from '@/components/shared/AdSense';
import AdFrame from '@/components/shared/AdFrame';
import NewsletterSignup from '@/components/shared/NewsletterSignup';

const Insights = () => {
  const baseUrl = 'https://wageatlas.com';
  const pageUrl = `${baseUrl}/insights/`;

  const featuredInsights = [
    {
      id: 1,
      title: "How to Read Salary Percentiles (and Why the Average Is Misleading)",
      description: "What the 10th, 25th, 50th, 75th, and 90th percentiles actually mean, why mean and median often disagree, and how to use the full wage distribution to judge a job offer.",
      category: "Salary Data",
      date: "2026-01-15",
      readTime: "9 min read",
      link: "/insights/how-to-read-salary-percentiles",
      tags: ["Percentiles", "Salary Data", "Job Offers"]
    },
    {
      id: 2,
      title: "Cost of Living vs. Salary: Why a $90k Job Can Beat a $120k Job",
      description: "How Regional Price Parities work, how to convert any salary into real purchasing power with one line of math, and where the simple adjustment breaks down.",
      category: "Salary Data",
      date: "2026-02-03",
      readTime: "10 min read",
      link: "/insights/cost-of-living-vs-salary",
      tags: ["Cost of Living", "RPP", "Relocation"]
    },
    {
      id: 3,
      title: "High-Paying Jobs Without a Degree: What the Data Actually Shows",
      description: "Using federal wage data to find no-degree career paths that genuinely reach $60k-$100k+, what they require instead of a diploma, and the trade-offs the listicles leave out.",
      category: "Career Paths",
      date: "2026-02-20",
      readTime: "11 min read",
      link: "/insights/high-paying-jobs-without-a-degree",
      tags: ["No Degree", "Skilled Trades", "Career Change"]
    },
    {
      id: 4,
      title: "Understanding Wage Trends: Growth, Volatility, and Recessions",
      description: "Nominal vs. real growth, why employment trends matter more than salary trends, and how different occupations behave when the economy turns.",
      category: "Market Analysis",
      date: "2026-03-10",
      readTime: "10 min read",
      link: "/insights/understanding-wage-trends",
      tags: ["Trends", "Recession", "Wage Growth"]
    },
    {
      id: 5,
      title: "The Salary Negotiation Playbook: Using Government Data to Get Paid What You're Worth",
      description: "Which numbers to pull, how to turn percentiles into a defensible ask, and the scripts that present data without sounding like an ultimatum.",
      category: "Career Advice",
      date: "2026-04-02",
      readTime: "12 min read",
      link: "/insights/salary-negotiation-guide",
      tags: ["Negotiation", "Career Advice", "Salary Data"]
    },
    {
      id: 6,
      title: "The AI Wage Premium: Do AI Skills Actually Pay More?",
      description: "Workers with AI skills earn roughly 62% more than same-role peers without them. What the PwC AI Jobs Barometer and BLS projections say about who benefits and how to position yourself.",
      category: "Market Analysis",
      date: "2026-07-21",
      readTime: "11 min read",
      link: "/insights/ai-wage-premium",
      tags: ["AI Economy", "Wage Premium", "Market Analysis"]
    }
  ];

  const categories = [
    { name: "All", count: featuredInsights.length },
    { name: "Salary Data", count: 2 },
    { name: "Career Paths", count: 1 },
    { name: "Market Analysis", count: 2 },
    { name: "Career Advice", count: 1 }
  ];

  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredInsights = selectedCategory === "All" 
    ? featuredInsights 
    : featuredInsights.filter(insight => insight.category === selectedCategory);

  // Generate structured data for the page
  const organizationSchema = generateOrganizationSchema(baseUrl);
  const articleSchemas = featuredInsights.map(insight => 
    generateArticleSchema(
      insight.title,
      insight.description,
      `${baseUrl}${insight.link}`,
      baseUrl,
      insight.date
    )
  );
  const structuredData = generateMultipleSchemas([organizationSchema, ...articleSchemas]);


  const handleInsightClick = (insight: typeof featuredInsights[0]) => {
    if (typeof window !== 'undefined' && (window as unknown).gtag) {
      (window as unknown).gtag('event', 'insight_view', {
        event_category: 'content',
        insight_title: insight.title,
        insight_category: insight.category,
      });
    }
  };

  return (
    <>
      <SeoHead
        title="Career Insights & Salary Analysis - Wage Atlas"
        description="Data-driven career insights, salary trends, and career advice. Discover high-paying jobs, negotiation strategies, and industry analysis powered by official BLS data."
        canonical={pageUrl}
        schemas={structuredData}
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-8">
              {/* Header */}
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <TrendingUp className="h-8 w-8 text-primary" />
                  <h1 className="text-3xl font-bold">Career Insights & Analysis</h1>
                </div>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Data-driven career advice, salary trends, and market insights powered by official 
                  U.S. Bureau of Labor Statistics data.
                </p>
              </div>

              {/* Leaderboard Ad */}
              <AdFrame className="mb-8">
                <LeaderboardAd />
              </AdFrame>

              {/* Flagship recurring report */}
              <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
                <Link to="/state-of-american-wages">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge>Recurring report</Badge>
                      <Badge variant="outline">Updated quarterly</Badge>
                    </div>
                    <CardTitle className="text-2xl">The State of American Wages</CardTitle>
                    <CardDescription className="text-base">
                      Our flagship data report: the highest-paying jobs, best careers without a degree, and
                      fastest-growing occupations - built on official BLS &amp; BEA data, free to read and cite.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="default" size="sm">
                      Read the latest edition <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Link>
              </Card>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2 justify-center">
                {categories.map(category => (
                  <Button
                    key={category.name}
                    variant={selectedCategory === category.name ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category.name)}
                  >
                    {category.name} ({category.count})
                  </Button>
                ))}
              </div>

              {/* Featured Insights Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredInsights.map(insight => (
                  <Card 
                    key={insight.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => handleInsightClick(insight)}
                  >
                    <Link to={insight.link}>
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <Badge variant="secondary">{insight.category}</Badge>
                          <span className="text-xs text-muted-foreground">{insight.readTime}</span>
                        </div>
                        <CardTitle className="text-lg mb-2 line-clamp-2">
                          {insight.title}
                        </CardTitle>
                        <CardDescription className="line-clamp-3">
                          {insight.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(insight.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mb-4">
                          {insight.tags.map(tag => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <Button variant="ghost" size="sm" className="w-full">
                          Read More <ArrowRight className="ml-2 h-3 w-3" />
                        </Button>
                      </CardContent>
                    </Link>
                  </Card>
                ))}
              </div>

              {/* In-Content Ad */}
              <InContentAd />

              {/* Newsletter Signup */}
              <NewsletterSignup 
                source="insights_page"
                title="Stay Updated with Career Insights"
                description="Get weekly career insights, salary trends, and job market analysis delivered to your inbox."
              />

              {/* Popular Tools Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Explore Our Tools
                  </CardTitle>
                  <CardDescription>
                    Use our data-driven tools to research salaries and plan your career.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Button asChild variant="outline" className="h-auto flex-col py-4">
                      <Link to="/search">
                        <Search className="h-5 w-5 mb-2" />
                        <span className="font-medium">Search Salaries</span>
                        <span className="text-xs text-muted-foreground mt-1">Find occupation data</span>
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="h-auto flex-col py-4">
                      <Link to="/trends">
                        <TrendingUp className="h-5 w-5 mb-2" />
                        <span className="font-medium">Wage Trends</span>
                        <span className="text-xs text-muted-foreground mt-1">Historical analysis</span>
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="h-auto flex-col py-4">
                      <Link to="/calculators">
                        <DollarSign className="h-5 w-5 mb-2" />
                        <span className="font-medium">Calculators</span>
                        <span className="text-xs text-muted-foreground mt-1">Salary conversions</span>
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="h-auto flex-col py-4">
                      <Link to="/best-states">
                        <MapPin className="h-5 w-5 mb-2" />
                        <span className="font-medium">Best States</span>
                        <span className="text-xs text-muted-foreground mt-1">Location comparison</span>
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Related Resources */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Career Resources</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Link to="/no-degree-jobs" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      No-Degree Jobs →
                    </Link>
                    <Link to="/best-paying-entry-level" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Entry-Level Jobs →
                    </Link>
                    <Link to="/industry" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Industry Analysis →
                    </Link>
                    <Link to="/rankings" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Occupation Rankings →
                    </Link>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Learn More</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Link to="/methodology" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Our Methodology →
                    </Link>
                    <Link to="/data-sources" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Data Sources →
                    </Link>
                    <Link to="/what-is-soc-code" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      SOC Code Guide →
                    </Link>
                    <Link to="/support" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Help & Support →
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Insights;
